import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useWishlist } from '../context/WishlistContext';
import {
  HiHome,
  HiWallet,
  HiHeart,
  HiUser,
} from 'react-icons/hi2';
import useAuth from "../hooks/useAuth"

/**
 * BottomNavigation - Mobile bottom navigation bar with quick access to main sections
 * 
 * Features:
 * - Quick access to Home, Wallet, Wishlist, Profile
 * - Badge for wishlist count
 * - Active state indication
 * - Sticky at bottom on mobile only
 */
const BottomNavigation = () => {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const { wishlistCount } = useWishlist();

  // Just navigate to search - no need for custom behavior
  // Let the search page handle its own state

  const navItems = [
    {
      path: '/',
      label: 'Home',
      icon: HiHome,
      exact: true,
    },
    {
      path: '/wallet',
      label: 'Wallet',
      icon: HiWallet,
      exact: false,
      requiresAuth: true,
    },
    {
      path: '/wishlist',
      label: 'Wishlist',
      icon: HiHeart,
      exact: false,
      badge: wishlistCount || "",
      requiresAuth: false, // Wishlist works without auth (localStorage)
    },
    {
      path: '/profile',
      label: 'Profile',
      icon: HiUser,
      exact: false,
      requiresAuth: true,
    },
  ];

  // Check if current path matches nav item
  const isActive = (item) => {
    if (item.exact) {
      return location.pathname === item.path;
    }
    return location.pathname.startsWith(item.path);
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 shadow-lg md:hidden">
      <div className="grid grid-cols-4 h-14">
        {navItems.map((item) => {
          // Filter items that require auth, but always show wishlist (it uses localStorage)
          if (item.requiresAuth && !isAuthenticated) {
            return null;
          }

          const Icon = item.icon;
          const active = isActive(item);
          const showBadge = item.badge && item.badge > 0;

          return (
            <Link
              key={item.path}
              to={item.path}
              className={`
                relative flex flex-col items-center justify-center
                transition-colors duration-200
                ${active
                  ? item.path === '/wishlist'
                    ? 'text-red-500 bg-red-50/50'
                    : 'text-orange-500 bg-orange-50/50'
                  : 'text-gray-600 hover:text-orange-500 hover:bg-gray-50'
                }
              `}
            >
              {/* Icon with Badge Overlay */}
              <div className="relative">
                <Icon className="text-2xl" />
                
                {/* Badge - Always show as overlay on top-right for wishlist */}
                {item.path === '/wishlist' && (
                  <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] flex items-center justify-center px-1 text-[10px] font-bold text-white bg-gradient-to-r from-red-500 to-pink-500 rounded-full border-2 border-white shadow-sm z-10">
                    {wishlistCount > 99 ? '99+' : wishlistCount}
                  </span>
                )}
                
                {/* Badge for other items (if needed in future) */}
                {showBadge && item.path !== '/wishlist' && (
                  <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] flex items-center justify-center px-1 text-[10px] font-bold text-white bg-gradient-to-r from-red-500 to-red-600 rounded-full border-2 border-white shadow-sm z-10">
                    {item.badge > 99 ? '99+' : item.badge}
                  </span>
                )}

                {/* Active Indicator Dot */}
                {active && (
                  <span className={`absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full ${
                    item.path === '/wishlist' ? 'bg-red-500' : 'bg-orange-500'
                  }`} />
                )}
              </div>

              {/* Label - Hide for Wishlist, show for others */}
              {item.path !== '/wishlist' && (
                <span
                  className={`
                    text-[10px] font-medium mt-0.5
                    ${active 
                      ? 'text-orange-500'
                      : 'text-gray-600'
                    }
                  `}
                >
                  {item.label}
                </span>
              )}

              {/* Active Background Indicator */}
              {active && (
                <div className={`absolute inset-0 bg-gradient-to-t ${
                  item.path === '/wishlist' 
                    ? 'from-red-50/80' 
                    : 'from-orange-50/80'
                } to-transparent pointer-events-none`} />
              )}
            </Link>
          );
        })}
      </div>

      {/* Safe Area Spacer for iOS */}
      <div className="h-safe-area-inset-bottom bg-white" />
    </nav>
  );
};

export default BottomNavigation;

